import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { trackException } from '@/lib/app-insights';
import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

/**
 * Friendly route-level error card mounted as the root `errorElement`. Surfaces
 * the error type without leaking stack traces to the user, with two recovery
 * affordances: navigate home (always renders) and reload (forces a fresh
 * router boot when the failure looks transient).
 */
export function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  let title = 'Something went wrong';
  let description = 'An unexpected error occurred while rendering this page.';

  if (isRouteErrorResponse(error)) {
    title = `${String(error.status)} ${error.statusText}`;
    description = typeof error.data === 'string' ? error.data : description;
  } else if (error instanceof Error) {
    description = error.message;
  }

  // Surface every route-level failure to App Insights. The deps array on
  // `error` means an in-place error swap (rare but possible when a route
  // re-throws after recovery) re-fires the track call. Non-Error values
  // (loader-thrown Response objects, strings) are wrapped so the AI envelope
  // gets a stable shape — losing the route-error status code there is
  // acceptable because the route's `requests` row will carry it.
  useEffect(() => {
    if (error == null) return;
    const err =
      error instanceof Error
        ? error
        : isRouteErrorResponse(error)
          ? new Error(`${String(error.status)} ${error.statusText}`)
          : new Error(typeof error === 'string' ? error : 'route error');
    trackException(err, { source: 'router' });
  }, [error]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Button
            onClick={() => {
              window.location.reload();
            }}
            variant="outline"
          >
            Reload
          </Button>
          <Button
            onClick={() => {
              void navigate('/');
            }}
          >
            Go to dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
